import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useValidationStore } from "@/state/validationState";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";

/*
 * Wire types come from the generated contract. The CALLS below stay on the
 * loosely typed view of the same client (see LooseClient).
 */
export type PackFileSummary = components["schemas"]["PackFileSummary"];
export type PackFileList = components["schemas"]["PackFileList"];
export type PackFileContent = components["schemas"]["PackFileContent"];
export type PutPackFileRequest = components["schemas"]["PutPackFileRequest"];
export type UndoPackFileRequest = components["schemas"]["UndoPackFileRequest"];
export type CompanionCheck = components["schemas"]["CompanionCheck"];
export type CompanionFix = components["schemas"]["CompanionFix"];

type LooseResult = { data?: unknown; error?: unknown; response: Response };
type LooseInit = {
  params?: { path?: Record<string, string>; query?: Record<string, string> };
  body?: unknown;
  signal?: AbortSignal;
};
/**
 * The generated client rejects paths it does not know at compile time, so until
 * the contract is regenerated this one view of it is loosely typed. It is the
 * SAME client: base URL, bearer header and sign-out gate are unchanged.
 */
interface LooseClient {
  GET(path: string, init: LooseInit): Promise<LooseResult>;
  PUT(path: string, init: LooseInit): Promise<LooseResult>;
  POST(path: string, init: LooseInit): Promise<LooseResult>;
  DELETE(path: string, init: LooseInit): Promise<LooseResult>;
}

const FILES = "/v1/projects/{projectId}/files";
const CONTENT = "/v1/projects/{projectId}/files/content";
const COMPANION_CHECK = "/v1/projects/{projectId}/files/companion-check";
const COMPANION_FIX = "/v1/projects/{projectId}/files/companion-fix";
const UNDO = "/v1/projects/{projectId}/files/undo";

function useLooseClient(): LooseClient {
  return useApiClient() as unknown as LooseClient;
}

export function usePackFilesQuery(
  projectId: string,
): UseQueryResult<PackFileList> {
  const client = useLooseClient();
  return useQuery({
    queryKey: queryKeys.packFiles(projectId),
    async queryFn({ signal }) {
      return unwrap(
        (await client.GET(FILES, {
          params: { path: { projectId } },
          signal,
        })) as { data?: PackFileList; error?: unknown; response: Response },
      );
    },
    enabled: projectId.length > 0,
  });
}

export function usePackFileQuery(
  projectId: string,
  path: string | null,
): UseQueryResult<PackFileContent> {
  const client = useLooseClient();
  return useQuery({
    queryKey: queryKeys.packFile(projectId, path ?? ""),
    async queryFn({ signal }) {
      return unwrap(
        (await client.GET(CONTENT, {
          params: { path: { projectId }, query: { path: path ?? "" } },
          signal,
        })) as { data?: PackFileContent; error?: unknown; response: Response },
      );
    },
    enabled: projectId.length > 0 && path !== null,
    // An open editor must not silently swap the text under the clinician.
    refetchOnWindowFocus: false,
  });
}

/** Never blocks, so a failed read is just no warnings: no retry. */
export function useCompanionCheckQuery(
  projectId: string,
): UseQueryResult<CompanionCheck> {
  const client = useLooseClient();
  return useQuery({
    queryKey: queryKeys.companionCheck(projectId),
    async queryFn({ signal }) {
      return unwrap(
        (await client.GET(COMPANION_CHECK, {
          params: { path: { projectId } },
          signal,
        })) as { data?: CompanionCheck; error?: unknown; response: Response },
      );
    },
    enabled: projectId.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** A saved or reverted file changes what a freeze exports: refresh readers. */
function useSettle(projectId: string) {
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);
  return async (saved: PackFileContent) => {
    clearValidation(projectId);
    queryClient.setQueryData(queryKeys.packFile(projectId, saved.path), saved);
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.packFiles(projectId),
      }),
      queryClient.invalidateQueries({ queryKey: queryKeys.draft(projectId) }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.previewCandidates(projectId),
      }),
    ]);
  };
}

export function useSavePackFileMutation(
  projectId: string,
): UseMutationResult<PackFileContent, unknown, PutPackFileRequest> {
  const client = useLooseClient();
  const settle = useSettle(projectId);
  return useMutation({
    async mutationFn(body) {
      return unwrap(
        (await client.PUT(CONTENT, {
          params: { path: { projectId } },
          body,
        })) as { data?: PackFileContent; error?: unknown; response: Response },
      );
    },
    onSuccess: settle,
    retry: false,
  });
}

export function useCompanionFixMutation(
  projectId: string,
): UseMutationResult<PackFileContent, unknown, CompanionFix> {
  const client = useLooseClient();
  const settle = useSettle(projectId);
  return useMutation({
    async mutationFn(body) {
      return unwrap(
        (await client.POST(COMPANION_FIX, {
          params: { path: { projectId } },
          body,
        })) as { data?: PackFileContent; error?: unknown; response: Response },
      );
    },
    onSuccess: settle,
    retry: false,
  });
}

export function useUndoPackFileMutation(
  projectId: string,
): UseMutationResult<PackFileContent, unknown, UndoPackFileRequest> {
  const client = useLooseClient();
  const settle = useSettle(projectId);
  return useMutation({
    async mutationFn(body) {
      return unwrap(
        (await client.POST(UNDO, {
          params: { path: { projectId } },
          body,
        })) as { data?: PackFileContent; error?: unknown; response: Response },
      );
    },
    onSuccess: settle,
    retry: false,
  });
}

export function useRevertPackFileMutation(
  projectId: string,
): UseMutationResult<
  PackFileContent,
  unknown,
  { path: string; expectedRevision?: number }
> {
  const client = useLooseClient();
  const settle = useSettle(projectId);
  return useMutation({
    async mutationFn({ path, expectedRevision }) {
      return unwrap(
        (await client.DELETE(CONTENT, {
          params: {
            path: { projectId },
            query: {
              path,
              ...(expectedRevision === undefined
                ? {}
                : { expectedRevision: String(expectedRevision) }),
            },
          },
        })) as { data?: PackFileContent; error?: unknown; response: Response },
      );
    },
    onSuccess: settle,
    retry: false,
  });
}

export type PackFileWriteFailure =
  | { kind: "stale" }
  | { kind: "invalid"; message: string }
  | { kind: "denied" }
  | { kind: "error"; message: string };

export function classifyPackFileWriteFailure(
  error: unknown,
): PackFileWriteFailure {
  if (error instanceof ApiError) {
    if (error.code === "stale_pack_file_revision") return { kind: "stale" };
    if (error.isForbidden || error.isUnauthenticated) return { kind: "denied" };
    // The server's own sentence ("YAML error on line N: ..."), verbatim.
    if (error.status === 400 || error.status === 413 || error.status === 422) {
      return { kind: "invalid", message: error.message };
    }
    return { kind: "error", message: error.message };
  }
  return { kind: "error", message: "The request could not be sent." };
}

/** Findings the freeze 422 carries, wherever the service put them. */
export function freezeFindings(
  error: unknown,
): { path: string; message: string }[] {
  if (!(error instanceof ApiError)) return [];
  const raw =
    (error.details as { findings?: unknown } | null | undefined)?.findings ??
    error.envelope["findings"];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item: unknown) => {
    if (typeof item !== "object" || item === null) return [];
    const { path, message } = item as Record<string, unknown>;
    return typeof message === "string"
      ? [{ path: typeof path === "string" ? path : "", message }]
      : [];
  });
}
